import axios from "axios";
import { GraphQLClient } from "graphql-request/dist";
import {
    getSdk as createSdk,
    SdkFunctionWrapper,
    UserProfile,
} from "../generated-interfaces/graphql";
import { saveAuthToken } from "./local-storage";
import logger from "./logger";
import { NodeEnv, nullOrUndefined } from "./types";

export const AUTH_TOKEN_RESPONSE_HEADER = "x-presto-auth-token";
const AUTHORIZATION_HEADER = "Authorization";
let client: GraphQLClient | null;

let authToken: string | nullOrUndefined;

export const setAuthToken = (token: string | nullOrUndefined) => {
    authToken = token;
    client = null; // Reset client so we use the new auth token
    saveAuthToken(token);
};

interface ApiRequestResult<T> {
    data: T & { responseStatusCode: number };
    headers: Headers;
    status: number;
}

const apiCallWrapper: SdkFunctionWrapper = async <T>(
    action: () => Promise<T>
): Promise<T> => {
    const startTime = new Date().getTime();
    const result: ApiRequestResult<T> = (await action()) as any;
    const requestDuration = new Date().getTime() - startTime;
    setAuthToken(result.headers.get(AUTH_TOKEN_RESPONSE_HEADER));
    result.data["responseStatusCode"] = result.status;
    logger.debug("API request duration (ms)", { requestDuration });
    return result.data;
};

export const getApiUrl = () => {
    const nodeEnv: NodeEnv =
        (process.env.REACT_APP_DEPLOY_ENV as NodeEnv) || process.env.NODE_ENV;

    switch (nodeEnv.toUpperCase()) {
        case "PRODUCTION":
            return "https://75tspoo2ql.execute-api.us-west-2.amazonaws.com/production";

        default:
            return "http://localhost:4000/dev";
    }
};

const axiosInstance = axios.create({
    baseURL: getApiUrl(),
    headers: {
        "Content-Type": "application/json",
    },
});

export const getSdk = () => {
    if (!client) {
        logger.debug("Created new GraphQL Client");
        client = new GraphQLClient(`${getApiUrl()}/graphql`);
        if (authToken) {
            client.setHeader(AUTHORIZATION_HEADER, authToken);
        }
    }
    return createSdk(client, apiCallWrapper);
};

export const validateGoogleCallback = (code: string, scope: string) => {
    return axiosInstance.get<UserProfile>("/auth/google/callback", {
        params: {
            code,
            scope,
        },
    });
};
